// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

// Pixel height of a Gantt bar. Rows are 8px taller than the bar so each
// bar has 4px of vertical padding top and bottom, giving the gradient
// connector lines a visible gap band to blend through.
export const GANTT_BAR_HEIGHT_PX = 24;
export const GANTT_ROW_HEIGHT_PX = GANTT_BAR_HEIGHT_PX + 8;
export const GANTT_BAR_VERTICAL_PADDING_PX =
  (GANTT_ROW_HEIGHT_PX - GANTT_BAR_HEIGHT_PX) / 2;

// Horizontal space (percent of axis) reserved at each end of the axis.
// Zero means the leftmost anchor sits flush against the container's left
// edge and the rightmost anchor sits flush against the right edge — the
// six segment bars fill 100% of the available width. Any zero-width or
// minWidth bar that would spill past the container is clipped via the
// containing box's `overflow: hidden`.
const GANTT_AXIS_PADDING_PCT = 0;

// Vertical space reserved above and below the Gantt bars for marker
// labels (Due, hover start/end labels). The label typography is ~11px
// high with a bit of padding; 20px is the clean minimum.
export const GANTT_MARKER_LABEL_BAND_PX = 20;

// Minimum pixel width of a rendered Gantt bar with positive duration.
// Axis padding scales with this constant so a minWidth bar at the extreme
// axis endpoints can't spill past the container's edge.
export const MIN_BAR_WIDTH_PX = 4;

// Rendering for zero-day segments (e.g. same-timestamp drafted + opened):
// show a 1px solid column so the observation is visible on the chart, and
// wrap it in a `ZERO_WIDTH_HIT_TARGET_PX`-wide transparent hit area so the
// tooltip has a hoverable target. The solid column sits in the middle of
// the hit area; the remaining space becomes 2px of transparent breathing
// room on each side.
export const ZERO_WIDTH_SOLID_PX = 1;
export const ZERO_WIDTH_HIT_TARGET_PX = 5;

// Approximate pixel width per character at the caption variant used
// inside Gantt bars. Combined with the bar's rendered pixel width and
// the display string length, this is how we decide whether the inline
// label fits or should be hidden behind the tooltip.
const CAPTION_CHAR_WIDTH_PX = 7;
const BAR_TEXT_PADDING_PX = 12;

// Fallback axis width when no paid-cycle data exists yet (e.g. empty
// dataset). Chosen as a reasonable 90-day upper bound so the aggregate
// Gantt renders a plausible scale even without real data.
export const FALLBACK_AXIS_DAYS = 90;

// Axis bounds: a leftmost day value and the total span visible on the
// chart. Consumed by `daysToPercent` when positioning bars. Bars at the
// extreme endpoints sit flush against the container edges; any overflow
// from `minWidth` or zero-width rendering is clipped via the container's
// `overflow: hidden`.
export interface GanttAxis {
  leftMostDay: number;
  axisSpanDays: number;
}

// Build a GanttAxis from raw observed day bounds. Falls back to a
// 1-day span when the observed range is empty so `daysToPercent` can't
// divide by zero.
export function buildAxis(axisMin: number, axisMax: number): GanttAxis {
  const rawSpan = axisMax - axisMin;
  return {
    leftMostDay: axisMin,
    axisSpanDays: rawSpan <= 0 ? 1 : rawSpan,
  };
}

// Convert a signed day offset to a percentage position on the Gantt's
// calendar-day axis. `leftMostDay` is the smallest offset visible on the
// chart; `axisSpanDays` is the total day range spanned.
export function daysToPercent(
  signedDayOffset: number,
  opts: GanttAxis
): number {
  if (opts.axisSpanDays <= 0) return GANTT_AXIS_PADDING_PCT;
  const rawPct =
    ((signedDayOffset - opts.leftMostDay) / opts.axisSpanDays) *
    (100 - 2 * GANTT_AXIS_PADDING_PCT);
  return GANTT_AXIS_PADDING_PCT + rawPct;
}

// Decide whether a day-value label should render inside a Gantt bar. We
// compare the bar's percentage width (which the caller converts to
// pixels using the axis container's observed width) against the string's
// estimated pixel width. When the bar is narrower than the text, return
// false and the caller skips the inline label — the tooltip on hover
// still shows it.
export function shouldShowBarLabel(
  widthPct: number,
  containerWidthPx: number,
  displayValue: string
): boolean {
  if (containerWidthPx <= 0) return false;
  const barPx = (widthPct / 100) * containerWidthPx;
  const textPx =
    displayValue.length * CAPTION_CHAR_WIDTH_PX + BAR_TEXT_PADDING_PX;
  return barPx >= textPx;
}
